import { IconButton } from 'components/v2/buttons';
import { StyledCopyIcon } from 'components/v2/epd-compiler-inputs/common-icons';
import { withActionState } from 'pages/v2/WithActionState';
import { AutoComplete } from 'primereact/autocomplete';
import { Panel } from 'primereact/panel';
import { RadioButton } from 'primereact/radiobutton';
import { TabView } from 'primereact/tabview';
import { ActionState, EPDCopyGroupStatus, VersionStateType } from 'services/EpdClient';
import styled, { css } from 'styled-components';

export const PanelContainer = styled.div`
  border-radius: 4px;
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.04);
`;

export const FormPanel = styled(PanelContainer)`
  justify-content: space-between;
  display: flex;
  width: 100%;
  align-items: center;
  margin-top: 16px;
  padding: 30px;
  background: #fff;
`;

export const ElementWithHelpBox = styled.div`
  display: grid;
  grid-row-gap: 16px;
  grid-template-columns: 1fr;
`;

export const HelpBoldText = styled.span`
  font-weight: 600;
`;

export const FieldPanel = styled(FormPanel)<{ state?: ActionState }>`
  display: ${(props) => (props?.state === ActionState.Hidden ? 'none' : 'grid')};

  visibility: ${(props) => (props?.state === ActionState.Hidden ? 'hidden' : 'visible')};

  grid-template-columns: 1fr;
  grid-column-gap: 30px;
  grid-row-gap: 30px;
  justify-content: start;

  ${(props) => props.theme.media.tablet} {
    grid-template-columns: 1fr 1fr;
  }
  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 1fr 1fr;
  }

  .w-full {
    grid-column: span 2;
  }
`;

export const FieldPanelFullWidth = styled(FieldPanel)`
  grid-template-columns: 1fr;
  grid-row-gap: 30px;
`;

export const ActionFieldPanel = withActionState(FieldPanel);
export const ActionFieldPanelFullWidth = withActionState(FieldPanelFullWidth);

export const FieldPanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  ${(props) => props.theme.typography.headerCaptionSemiBold}
`;

export const FieldContainer = styled.div`
  margin-top: 22px;
`;

export const HelpBoxContainer = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 16px;
`;

export const FieldPanelInput = styled.div`
  width: 100%;
  padding: 0px;
  display: grid;
  align-self: start;
`;

export const DoubleFieldPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 30px;
  grid-row-gap: 0px;
  justify-content: start;

  ${(props) => props.theme.media.tablet} {
    grid-template-columns: 1fr 1fr;
  }

  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 1fr 1fr;
  }
`;

export const DoubleFieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 30px;
  grid-row-gap: 30px;
  justify-content: start;

  ${(props) => props.theme.media.tablet},
  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 1fr 1fr;
    .w-full {
      grid-column: span 2;
    }
  }
`;

export const HeaderCaptionSemiBold = styled.label`
  ${(props) => props.theme.typography.headerCaptionSemiBold}
`;

export const HeaderCaptionSemiBoldNormalCase = styled.label`
  ${(props) => props.theme.typography.headerCaptionSemiBold} {
    text-transform: none;
  }
`;

export const CheckboxLabel = styled.label`
  font-family: 'Inter';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: ${(props) => props.theme.colors.primaryBlack};
`;

export const Step = styled.div`
  ${(props) => props.theme.typography.headerCaptionSemiBold}

  display: flex;
  flex-direction: row;
  padding: 1rem 1rem;
  margin-left: -1rem;
  margin-right: -1rem;
  border-radius: 4px;
  justify-content: space-between;
  align-items: center;

  &:hover {
    cursor: pointer;
    background-color: ${(props) => props.theme.colors.lightGray};
  }
`;

export const CheckBoxPanel = styled.div`
  text-align: start;
  margin-top: 1rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0.75rem;
  width: 100%;
  background-color: rgba(231, 81, 19, 0.1);
  border-radius: 2px;
  height: 44px;
`;

export const EPDStatusBadge = styled.div<{ status?: VersionStateType }>`
  ${(props) => props.theme.typography.headerCaptionMedium};
  white-space: nowrap;
  height: 22px;
  padding: 0.25rem 0.75rem;
  border-radius: 2px;
  color: ${(props) => props.theme.colors.primaryBlack80};
  background-color: ${(props) => props.theme.colors.statusLabelGrayBg};

  ${(props) =>
    props.status === VersionStateType.UnderReview &&
    css`
      color: ${(props) => props.theme.colors.statusLabelYellowText};
      background-color: ${(props) => props.theme.colors.statusLabelYellowBg};
    `}

  ${(props) =>
    (props.status === VersionStateType.Approved ||
      props.status === VersionStateType.SentForPublicationEditorial ||
      props.status === VersionStateType.SentForPublicationLicenseeAdmin) &&
    css`
      color: ${(props) => props.theme.colors.statusLabelGreenText};
      background-color: ${(props) => props.theme.colors.statusLabelGreenBg};
    `}

  ${(props) =>
    props.status === VersionStateType.DeclinedVerification &&
    css`
      color: ${(props) => props.theme.colors.statusLabelRedText};
      background-color: ${(props) => props.theme.colors.statusLabelRedBg};
    `}
`;

export const EPDGroupStatusBadge = styled.div<{ status?: EPDCopyGroupStatus }>`
  ${(props) => props.theme.typography.headerCaptionMedium};
  margin-left: 1.5rem;
  border-radius: 2px;
  display: inline-flex;
  height: 22px;
  padding: 4px 10px;
  align-items: flex-start;
  white-space: nowrap;

  color: ${(props) => props.theme.colors.primaryBlack80};
  background-color: ${(props) => props.theme.colors.statusLabelGrayBg};

  ${(props) =>
    (props.status === EPDCopyGroupStatus.SentForVerification ||
      props.status === EPDCopyGroupStatus.Verified ||
      props.status === EPDCopyGroupStatus.SentForPublication ||
      props.status === EPDCopyGroupStatus.ApprovedByLicenseeAdmin) &&
    css`
      color: ${(props) => props.theme.colors.statusLabelYellowText};
      background-color: ${(props) => props.theme.colors.statusLabelYellowBg};
    `}

  ${(props) =>
    (props.status === EPDCopyGroupStatus.DeclinedPublication ||
      props.status === EPDCopyGroupStatus.DeclinedCopyByLicenseeAdmin) &&
    css`
      color: ${(props) => props.theme.colors.statusLabelRedText};
      background-color: ${(props) => props.theme.colors.statusLabelRedBg};
    `}
`;

export const EPDGroupCardDashboardStatus = styled(EPDGroupStatusBadge)`
  margin-top: 4px;
  margin-left: 0;
  ${(props) =>
    (props.status === EPDCopyGroupStatus.Draft || props.status === EPDCopyGroupStatus.Reserved) &&
    css`
      color: ${(props) => props.theme.colors.primaryWhite};
      background-color: ${(props) => props.theme.colors.darkGray};
    `}

  font-family: Roboto;
  font-size: 12px;
  font-style: italic;
  font-weight: 500;
  line-height: 14.06px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;

  text-transform: none;
`;

export const InfoBox = styled.div`
  display: flex;
  padding: 1rem;
  border-left: solid 3px ${(props) => props.theme.colors.orange};
  margin: 0.5rem 0;
  background-color: #f3f3f3;
  margin-bottom: 3rem;

  @media print {
    display: none;
  }
`;

export const ErrorTextBox = styled.div`
  position: relative;
  border-radius: 4px;
  padding: 1rem 3rem 1rem 2rem;
  flex-shrink: 0;
  margin-bottom: 1rem;
  background-color: ${(props) => props.theme.colors.statusLabelRedBg};
  ${(props) => props.theme.typography.statusLabelsRedText}

  @media print {
    display: none;
  }
`;

export const MainViewFull = styled.div`
  flex: 0 0 100%;

  ${ErrorTextBox} {
    word-break: break-word;
  }
`;

export const HorizontalDivider = styled.div`
  margin: 30px 0 0 0;
  border-bottom: 2px solid ${(props) => props.theme.colors.elementsInputBorder};
`;

export const WizardHeaderGrid = styled.div`
  display: grid;
  width: 100%;
  margin-bottom: 40px;
  align-items: center;
  grid-template-columns: 35px 1fr auto;
`;

export const WizardHeaderButtonBox = styled.div`
  display: flex;
  flex-wrap: nowrap;
  margin-left: 16px;
  justify-self: flex-end;
`;

export const StyledInput = styled.input`
  padding: 1rem;
  border: 1px solid
    ${(props) => (props.disabled ? props.theme.colors.elementsFilledInput : props.theme.colors.elementsInputBorder)};
  border-radius: 2px;
  margin: 0;
  height: 44px;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.elementsFilledInput : props.theme.colors.primaryWhite};

  ${(props) => props.theme.typography.bodyInput};
  color: ${(props) => props.theme.colors.primaryBlack};

  &:focus {
    border-color: ${(props) => (props.disabled ? 'transparent' : props.theme.colors.primaryOrange)};
  }

  &:focus-visible {
    outline: none;
  }

  &:hover {
    border-color: ${(props) => (props.disabled ? 'transparent' : props.theme.colors.primaryOrange)};
  }

  &:disabled {
    color: ${(props) => props.theme.colors.primaryBlack70};
  }
`;

export const StyledAutocomplete = styled(AutoComplete)`
  .p-inputtext {
    padding: 1rem;
    border: 1px solid
      ${(props) => (props.disabled ? props.theme.colors.elementsFilledInput : props.theme.colors.elementsInputBorder)};
    border-radius: 2px;
    margin: 0;
    height: 44px;
    width: 100%;

    &:focus {
      border-color: ${(props) => (props.disabled ? 'transparent' : props.theme.colors.primaryOrange)};
      box-shadow: none;
    }

    &:focus-visible {
      outline: none;
    }

    &:hover {
      border-color: ${(props) => (props.disabled ? 'transparent' : props.theme.colors.primaryOrange)};
    }

    background-color: ${(props) =>
      props.disabled ? props.theme.colors.elementsFilledInput : props.theme.colors.primaryWhite};
    color: ${(props) => (props.disabled ? props.theme.colors.primaryBlack70 : props.theme.colors.black)};

    ${(props) => props.theme.typography.bodyInput};
  }
`;

export const ExpandablePanel = styled(Panel)`
  border: 0;
  && .p-panel-header {
    padding-left: 30px;
    padding-right: 30px;
    border: 0;
    background-color: #ffffff;
    margin-top: 1rem;
    min-height: 70px;
  }
  .p-panel-content {
    padding-top: 0;
    padding-left: 30px;
    padding-right: 30px;
    padding-bottom: 30px;
    border: 0;
    background-color: ${(props) => props.theme.colors.primaryWhite};
    width: 100%;
    display: grid;
  }

  &.multi-container > .p-toggleable-content > .p-panel-content {
    padding: 0;
    border: 0;
    background-color: transparent;
    width: 100%;
    display: grid;
  }
`;

export const ExpandablePanelHeaderName = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  ${HeaderCaptionSemiBold} {
    text-transform: uppercase;
  }
`;

export const EpdItemPanel = styled(Panel)`
  border-radius: 4px;
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.04);
  border: 0;
  .p-panel-header {
    border: 0;
    border-radius: 4px;
  }
  .p-panel-content {
    border: 0;
    margin-top: -4px;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    background-color: ${(props) => props.theme.colors.primaryWhite};
    padding-left: 5rem;
  }
`;

export const TabViewStyled = styled(TabView)`
  .p-tabview-nav-container {
    height: 48px;
  }
  .p-tabview-panels {
    background-color: unset;
    padding: 0;
  }
  .p-tabview-nav {
    border-bottom: 2px solid ${(props) => props.theme.colors.elementsInputBorder};
  }

  &&& .p-tabview-nav-link {
    border: none;
    background-color: transparent;
    color: ${(props) => props.theme.colors.elementsButtonDisabled};
    padding-top: 15px;
    padding-bottom: 15px;
  }

  &&& .p-tabview-nav-link > * {
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 18px; /* 128.571% */
    letter-spacing: -0.28px;
  }

  &&& li.p-highlight .p-tabview-nav-link {
    background-color: transparent;
    color: ${(props) => props.theme.colors.primaryGreen};
    border-bottom: 2px solid ${(props) => props.theme.colors.primaryGreen};
  }
`;

export const RadioButtonStyled = styled(RadioButton)`
  &.p-radiobutton .p-radiobutton-box {
    align-self: center;
    width: 14px;
    height: 14px;
  }
  &.p-radiobutton .p-radiobutton-box .p-radiobutton-icon {
    width: 8px;
    height: 8px;
  }
  &.p-radiobutton .p-radiobutton-box.p-highlight,
  &.p-radiobutton .p-radiobutton-box.p-highlight:not(.p-disabled):hover {
    background: ${(props) => props.theme.colors.primaryWhite};
    border-color: ${(props) => props.theme.colors.primaryOrange};
  }
  &.p-radiobutton .p-radiobutton-box:not(.p-disabled).p-focus {
    box-shadow: 0 0 0 0.1rem rgba(231, 81, 19, 0.5);
    border-color: ${(props) => props.theme.colors.primaryOrange};
  }
  &.p-radiobutton .p-radiobutton-box.p-highlight .p-radiobutton-icon {
    background-color: ${(props) => props.theme.colors.primaryOrange};
  }
  ${(props) =>
    props.disabled &&
    css`
      cursor: default;
    `}
`;

export const ExpandablePanelModule = styled(ExpandablePanel)`
  & .p-panel-content {
    padding-top: 0;
    margin-top: 0;
    grid-template-columns: 1fr;
    grid-row-gap: 30px;
  }
`;

export const IconsSection = styled.div`
  display: flex;
  align-items: center;

  ${StyledCopyIcon} {
    margin-left: 1rem;
  }
`;

export const StyledIconButton = styled(IconButton)`
  margin-left: 0.25rem;
  &.p-button.p-button-text {
    color: ${(props) => props.theme.colors.primaryBlack};
  }
  &.p-button.p-button-text:hover {
    background: transparent;
  }
`;

export const TitleIconBar = styled.div`
  display: flex;
  align-self: auto;
  align-items: center;
  gap: 4px;
`;

export const GrayHR = styled.hr`
  border: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
`;
